/**
 * lib/syncQueue.js — Iter 36 Offline Field Mode write queue.
 *
 * Persists outbound mutations (photo captures, measurements,
 * acknowledgements) to IndexedDB so the user can keep working through a
 * dead zone and have everything sync automatically when connectivity
 * returns. Designed to be invisible — the user never sees IDB, never
 * sees a "Sync" button, never picks a conflict resolution strategy.
 *
 * Op shape (generic; future-compatible with notes/markups/issues):
 *   {
 *     id: string,              // client-generated stable id
 *     op_type: string,         // 'photo.capture' | 'measurement.create' | 'ack.update'
 *     endpoint: string,        // '/sheets/{id}/photos'
 *     method: 'POST'|'PATCH'|'DELETE',
 *     payload: any,            // JSON body (for non-multipart)
 *     form_fields: object?,    // multipart fields (when method=POST + blob present)
 *     blob: Blob?,             // multipart file blob (photo capture)
 *     filename: string?,
 *     content_type: string?,
 *     created_at: string,
 *     attempts: number,
 *     last_error: string?,
 *     // optimistic-UI hints:
 *     scope_key: string?,      // e.g. 'photos:structure:str_xxx' so the UI can
 *                              // surface pending rows alongside server rows.
 *     optimistic: object?,     // a doc-shaped local row to render NOW.
 *   }
 *
 * Drain trigger:
 *   - `online` window event
 *   - app boot
 *   - explicit drainNow() on any successful write (probes connectivity)
 *
 * NEVER throws. The queue is best-effort and self-healing.
 */
const DB_NAME = "fop_sync_queue_v1";
const STORE = "ops";

function _openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("no IDB"));
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "id" });
        s.createIndex("created_at", "created_at");
        s.createIndex("scope_key", "scope_key");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function _uid() {
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

async function _tx(mode, work) {
  try {
    const db = await _openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      let out;
      try { out = work(store); } catch (e) { reject(e); return; }
      tx.oncomplete = () => { db.close(); resolve(out); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.onabort = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    return null;
  }
}

export async function enqueue(op) {
  const entry = {
    id: op.id || _uid(),
    created_at: op.created_at || new Date().toISOString(),
    attempts: 0,
    last_error: null,
    ...op,
  };
  const result = await _tx("readwrite", (s) => s.put(entry));
  // GH-20 (real-plan certification, 2026-10-01) — `_tx` already
  // distinguishes a genuinely failed write (IndexedDB unavailable,
  // quota exceeded, private-browsing restrictions, etc.) by resolving
  // `null` instead of rejecting. This function used to ignore that and
  // return `entry` unconditionally, so every caller believed the write
  // was durably queued even when NOTHING was actually written to disk
  // — the silent-total-loss half of GH-20 (network AND durable storage
  // both failed, with no signal either way). Callers that must know
  // (e.g. `useMeasurementController`) check for a `null` return;
  // everything else keeps its existing "fire and forget" behavior
  // unchanged (a `null` here was already indistinguishable from any
  // other outcome for callers that don't check it).
  if (result === null) return null;
  _emit();
  return entry;
}

export async function listAll() {
  return new Promise(async (resolve) => {
    try {
      const db = await _openDb();
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => { db.close(); resolve(req.result || []); };
      req.onerror = () => { db.close(); resolve([]); };
    } catch {
      resolve([]);
    }
  });
}

export async function listByScope(scopeKey) {
  const all = await listAll();
  return all.filter((e) => e.scope_key === scopeKey);
}

export async function remove(id) {
  await _tx("readwrite", (s) => s.delete(id));
  _emit();
}

export async function update(id, patch) {
  await _tx("readwrite", (s) => {
    const r = s.get(id);
    r.onsuccess = () => {
      const v = r.result;
      if (v) s.put({ ...v, ...patch });
    };
  });
  _emit();
}

export async function count() {
  const all = await listAll();
  return all.length;
}

// ----- pub/sub -----
const listeners = new Set();
export function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function _emit() {
  listeners.forEach((fn) => { try { fn(); } catch {/* */} });
}

// ----- drain -----
let _draining = false;
let _draining_failure = false;
let _api = null;

/**
 * Provide the axios client. Called once from App on boot. The queue
 * doesn't import axios directly to keep this module ESM-pure for the
 * service-worker side if we ever extend that direction.
 */
export function setSyncClient(api) {
  _api = api;
}

/**
 * Attempt to flush every queued op. Per directive:
 *   - Most-recent-wins for now (no per-op conflict strategy yet).
 *   - Multipart blobs are rebuilt from the stored Blob.
 *   - Failed ops bump `attempts`, keep `last_error`, stay queued.
 *   - Permanent client-error responses (4xx other than 408/429) discard the op
 *     so we don't loop forever on malformed payloads.
 */
export async function drainNow() {
  if (_draining || !_api) return { succeeded: 0, failed: 0, skipped: 0 };
  _draining = true;
  _draining_failure = false;
  _emit();
  let succeeded = 0;
  let failed = 0;
  let dropped = 0;
  try {
    const ops = (await listAll()).sort((a, b) => a.created_at.localeCompare(b.created_at));
    for (const op of ops) {
      try {
        await _send(op);
        await remove(op.id);
        succeeded += 1;
      } catch (e) {
        const status = e?.response?.status || 0;
        const permanent =
          (status >= 400 && status < 500 && status !== 408 && status !== 425 && status !== 429);
        if (permanent) {
          // The server rejected the payload conclusively (auth, validation,
          // missing FK after deletion). Dropping here prevents an infinite
          // retry storm. The audit record on the server captured the attempt.
          await remove(op.id);
          dropped += 1;
        } else {
          await update(op.id, {
            attempts: (op.attempts || 0) + 1,
            last_error: e?.message || String(e),
          });
          failed += 1;
          _draining_failure = true;
        }
      }
    }
  } finally {
    _draining = false;
    _emit();
  }
  return { succeeded, failed, dropped };
}

async function _send(op) {
  if (op.method === "POST" && op.blob) {
    // Multipart photo upload — reconstruct FormData from the stored fields + Blob.
    const form = new FormData();
    Object.entries(op.form_fields || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined) form.append(k, String(v));
    });
    form.append("file", new File([op.blob], op.filename || "photo.jpg", { type: op.content_type || "image/jpeg" }));
    // Founder Continuation (2026-09-08) — `op.id` is generated once at
    // enqueue() and stays identical across every retry of THIS entry (a
    // genuinely new capture always gets a new enqueue() call, so this
    // never collapses two separate photos). Lets the server recognize a
    // retry that already succeeded server-side but whose response the
    // client lost, instead of creating a second photo + billing storage
    // twice. See routers/photos.py `client_op_id`.
    form.append("client_op_id", op.id);
    return await _api.post(op.endpoint, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  if (op.method === "POST") return await _api.post(op.endpoint, op.payload || {});
  if (op.method === "PATCH") return await _api.patch(op.endpoint, op.payload || {});
  if (op.method === "DELETE") return await _api.delete(op.endpoint);
  throw new Error(`Unknown method ${op.method}`);
}

/**
 * Status snapshot for the operational pill. Returns one of:
 *   { state: 'synced', pending: 0 }
 *   { state: 'pending', pending: N }
 *   { state: 'syncing', pending: N }
 *   { state: 'error', pending: N }
 *   { state: 'offline', pending: N }
 */
export async function snapshot() {
  const n = await count();
  const online = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
  if (_draining) return { state: "syncing", pending: n };
  if (!online && n > 0) return { state: "offline", pending: n };
  if (n === 0) return { state: "synced", pending: 0 };
  if (_draining_failure) return { state: "error", pending: n };
  return { state: "pending", pending: n };
}

// ----- auto-drain wiring -----
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { drainNow(); });
  // Periodic background probe — every 30s while there's pending work.
  setInterval(async () => {
    const n = await count();
    if (n > 0 && navigator.onLine !== false) drainNow();
  }, 30_000);
}
